import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useReducer, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { TOKEN_KEY } from '@/api/index';
import meKeys from '@/api/queries/me/keyFactory';
import useGetChatAgentConversation from '@/api/queries/me/useGetChatAgentConversation';
import { getNewWorldTokenLocalStorageKey } from '@/helpers/localStorageKeyHelper';
import {
  CHAT_AGENT_EVENT_TYPES,
  type ChatAgentEvent,
  type ChatAgentEventType,
  type ChatAgentStatusCode,
} from '@payaca/types/chatAgentTypes';
import { assertUnreachable } from '@payaca/utilities/guards';

import { transformPersistedMessage } from './messageAdapter';
import type { ChatMessage, ContentBlock, ToolUse } from './types';

// Generate unique IDs - UUIDs for conversation IDs (server-validated), simple IDs for messages
const generateConversationId = () => uuid();
const generateMessageId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

const API_HOST = import.meta.env.VITE_API_HOST as string;

export interface PageContext {
  pathname?: string;
  searchParams?: Record<string, string>;
  mountedQueryKeys?: unknown[][];
}

interface UseChatStreamOptions {
  onNewChatConfirmation?: () => void;
  onMutationComplete?: () => void;
  onStreamComplete?: () => void;
  initialConversationId?: string;
}

interface UseChatStreamReturn {
  title?: string;
  summary?: string;
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  isDisabled: boolean;
  status: ChatAgentStatusCode | null;
  conversationId: string | null;
  isLoading: boolean;
  streamingConversationIds: Set<string>;
  sendMessage: (message: string, context?: PageContext) => Promise<void>;
  clearMessages: () => void;
  switchConversation: (conversationId: string | null) => void;
}

// --- State & Actions ---

/** State for a single conversation */
interface ConversationState {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  isDisabled: boolean;
  status: ChatAgentStatusCode | null;
}

/** Multi-conversation state indexed by conversationId */
interface ChatState {
  conversations: Map<string, ConversationState>;
  activeConversationId: string | null;
}

const createEmptyConversationState = (): ConversationState => ({
  messages: [],
  isStreaming: false,
  error: null,
  isDisabled: false,
  status: null,
});

const initialState: ChatState = {
  conversations: new Map(),
  activeConversationId: null,
};

type ChatAction =
  /** Adds user message, creates assistant placeholder, clears error, starts streaming. */
  | { type: 'SEND_MESSAGE'; conversationId: string; userMessage: string }
  /** Appends streaming text to assistant message content and contentBlocks. */
  | { type: 'APPEND_TEXT'; conversationId: string; text: string }
  /** Adds tool to assistant message if not already present (idempotent). */
  | {
      type: 'REGISTER_TOOL';
      conversationId: string;
      toolUseId: string;
      toolName: string;
    }
  /** Updates tools with final input data and marks them complete. */
  | {
      type: 'FINALIZE_TOOLS';
      conversationId: string;
      toolUses: Array<{
        id: string;
        name: string;
        input: Record<string, unknown>;
      }>;
    }
  /** Stream finished without error. Marks streaming complete. */
  | { type: 'STREAM_COMPLETE'; conversationId: string }
  /** Stream failed. Sets error, removes empty assistant message, stops streaming. */
  | { type: 'STREAM_ERROR'; conversationId: string; error: string }
  /** User cancelled request. Stops streaming silently. */
  | { type: 'STREAM_ABORTED'; conversationId: string }
  /** Clears all conversations and resets to initial state. */
  | { type: 'RESET' }
  /** User switches to a different conversation. */
  | {
      type: 'SWITCH_CONVERSATION';
      newConversationId: string | null;
      previousConversationId: string | null;
      clearPrevious: boolean;
    }
  /** Server confirms a new conversation was created. */
  | { type: 'NEW_CONVERSATION_CONFIRMED'; conversationId: string }
  /** Context window exhausted - disable further messages. */
  | { type: 'CONTEXT_EXHAUSTED'; conversationId: string; error: string }
  /** Sets system status (e.g., compacting). */
  | {
      type: 'SET_STATUS';
      conversationId: string;
      status: ChatAgentStatusCode | null;
    };

// --- Pure Helper Functions ---

function appendTextToBlocks(
  blocks: ContentBlock[],
  text: string
): ContentBlock[] {
  const result = [...blocks];
  const lastBlock = result[result.length - 1];

  if (lastBlock?.type === 'text') {
    result[result.length - 1] = { type: 'text', text: lastBlock.text + text };
  } else {
    result.push({ type: 'text', text });
  }

  return result;
}

function ensureToolInMessage(
  toolUses: ToolUse[],
  contentBlocks: ContentBlock[],
  toolUseId: string,
  toolName: string
): { toolUses: ToolUse[]; contentBlocks: ContentBlock[] } {
  const exists = toolUses.some((t) => t.id === toolUseId);
  if (exists) {
    return { toolUses, contentBlocks };
  }

  return {
    toolUses: [
      ...toolUses,
      { id: toolUseId, name: toolName, input: {}, status: 'running' },
    ],
    contentBlocks: [...contentBlocks, { type: 'tool', toolId: toolUseId }],
  };
}

function completeTools(
  toolUses: ToolUse[],
  contentBlocks: ContentBlock[],
  finalTools: Array<{
    id: string;
    name: string;
    input: Record<string, unknown>;
  }>
): { toolUses: ToolUse[]; contentBlocks: ContentBlock[] } {
  const updatedToolUses = [...toolUses];
  let updatedBlocks = contentBlocks;

  for (const newTool of finalTools) {
    const existingIndex = updatedToolUses.findIndex((t) => t.id === newTool.id);

    if (existingIndex >= 0) {
      updatedToolUses[existingIndex] = {
        ...updatedToolUses[existingIndex],
        input: newTool.input,
        status: 'complete',
      };
    } else {
      updatedToolUses.push({
        id: newTool.id,
        name: newTool.name,
        input: newTool.input,
        status: 'complete',
      });
      updatedBlocks = [...updatedBlocks, { type: 'tool', toolId: newTool.id }];
    }
  }

  return { toolUses: updatedToolUses, contentBlocks: updatedBlocks };
}

function updateLastAssistantMessage(
  messages: ChatMessage[],
  updater: (msg: ChatMessage) => ChatMessage
): ChatMessage[] {
  const lastMsg = messages[messages.length - 1];
  if (lastMsg?.role !== 'assistant') return messages;

  const result = [...messages];
  result[result.length - 1] = updater(lastMsg);
  return result;
}

// --- Reducer Helpers ---

/** Get conversation state, creating empty if not exists */
function getConversation(
  conversations: Map<string, ConversationState>,
  conversationId: string
): ConversationState {
  return conversations.get(conversationId) ?? createEmptyConversationState();
}

/** Update a specific conversation in the Map */
function updateConversation(
  state: ChatState,
  conversationId: string,
  updater: (conv: ConversationState) => ConversationState
): ChatState {
  const conversations = new Map(state.conversations);
  const current = getConversation(conversations, conversationId);
  conversations.set(conversationId, updater(current));
  return { ...state, conversations };
}

// --- Reducer ---

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SEND_MESSAGE': {
      const next = updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: [
          ...conv.messages,
          {
            id: generateMessageId(),
            role: 'user',
            content: action.userMessage,
            timestamp: new Date(),
          },
          {
            id: generateMessageId(),
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            isStreaming: true,
            contentBlocks: [],
            toolUses: [],
          },
        ],
        isStreaming: true,
        error: null,
        status: null,
      }));
      return { ...next, activeConversationId: action.conversationId };
    }

    case 'APPEND_TEXT':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: updateLastAssistantMessage(conv.messages, (msg) => ({
          ...msg,
          content: msg.content + action.text,
          contentBlocks: appendTextToBlocks(
            msg.contentBlocks ?? [],
            action.text
          ),
        })),
      }));

    case 'REGISTER_TOOL':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: updateLastAssistantMessage(conv.messages, (msg) => {
          const { toolUses, contentBlocks } = ensureToolInMessage(
            msg.toolUses ?? [],
            msg.contentBlocks ?? [],
            action.toolUseId,
            action.toolName
          );
          return { ...msg, toolUses, contentBlocks };
        }),
      }));

    case 'FINALIZE_TOOLS':
      if (!action.toolUses.length) return state;
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: updateLastAssistantMessage(conv.messages, (msg) => {
          const { toolUses, contentBlocks } = completeTools(
            msg.toolUses ?? [],
            msg.contentBlocks ?? [],
            action.toolUses
          );
          return { ...msg, toolUses, contentBlocks };
        }),
      }));

    case 'STREAM_COMPLETE':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        isStreaming: false,
        status: null,
        messages: updateLastAssistantMessage(conv.messages, (msg) => ({
          ...msg,
          isStreaming: false,
        })),
      }));

    case 'STREAM_ERROR':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: updateLastAssistantMessage(
          conv.messages.filter((m) => m.content.length > 0),
          (msg) => ({ ...msg, isStreaming: false })
        ),
        isStreaming: false,
        error: action.error,
        status: null,
      }));

    case 'STREAM_ABORTED':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: updateLastAssistantMessage(conv.messages, (msg) => ({
          ...msg,
          isStreaming: false,
        })),
        isStreaming: false,
        status: null,
      }));

    case 'RESET':
      return initialState;

    case 'SWITCH_CONVERSATION': {
      let conversations = state.conversations;
      if (action.clearPrevious && action.previousConversationId) {
        conversations = new Map(conversations);
        conversations.delete(action.previousConversationId);
      }
      return {
        ...state,
        conversations,
        activeConversationId: action.newConversationId,
      };
    }

    case 'NEW_CONVERSATION_CONFIRMED':
      return {
        ...state,
        activeConversationId: action.conversationId,
      };

    case 'CONTEXT_EXHAUSTED':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        messages: updateLastAssistantMessage(
          conv.messages.filter((m) => m.content.length > 0),
          (msg) => ({ ...msg, isStreaming: false })
        ),
        isStreaming: false,
        error: action.error,
        isDisabled: true,
        status: null,
      }));

    case 'SET_STATUS':
      return updateConversation(state, action.conversationId, (conv) => ({
        ...conv,
        status: action.status,
      }));

    default:
      return state;
  }
}

// --- Server-Sent Events Stream Parser ---

async function* parseServerSentEventsStream(
  reader: ReadableStreamDefaultReader<Uint8Array>
): AsyncGenerator<ChatAgentEvent> {
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEventType = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEventType = line.slice(7).trim();
        continue;
      }

      if (line.startsWith('data: ')) {
        try {
          const data: unknown = JSON.parse(line.slice(6));
          if (currentEventType && isValidEventType(currentEventType)) {
            yield { type: currentEventType, data } as ChatAgentEvent;
          }
        } catch {
          // Ignore malformed JSON
        }
      }
    }
  }
}

function isValidEventType(type: string): type is ChatAgentEventType {
  return CHAT_AGENT_EVENT_TYPES.includes(type as ChatAgentEventType);
}

// --- Main Hook ---

export const useChatStream = (
  options?: UseChatStreamOptions
): UseChatStreamReturn => {
  const queryClient = useQueryClient();

  // Initialize state with initial conversation ID if provided
  const [state, dispatch] = useReducer(chatReducer, {
    conversations: new Map(),
    activeConversationId: options?.initialConversationId ?? null,
  });

  // Map of abort controllers per conversation
  const abortControllersRef = useRef(new Map<string, AbortController>());

  // Active conversation ID from state
  const activeConversationId = state.activeConversationId;

  // Fetch persisted conversation from cache/server
  const { data: conversationData, isLoading } = useGetChatAgentConversation(
    activeConversationId,
    {
      // During active session, messages are stored in reducer - prevent auto-refetch
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  );

  // Get active conversation state from reducer
  const activeConversation = activeConversationId
    ? state.conversations.get(activeConversationId)
    : undefined;

  // Derive final messages: TanStack persisted + reducer session messages
  const messages = useMemo((): ChatMessage[] => {
    // 1. Get persisted messages from TanStack (previous sessions)
    const persisted = conversationData?.chatAgentConversation?.messages ?? [];
    const result: ChatMessage[] = persisted.map(transformPersistedMessage);

    // 2. Append current session messages from reducer
    const sessionMessages = activeConversation?.messages ?? [];
    result.push(...sessionMessages);

    return result;
  }, [conversationData, activeConversation?.messages]);

  // Derive streaming conversation IDs
  const streamingConversationIds = useMemo(() => {
    const ids = new Set<string>();
    for (const [id, conv] of state.conversations) {
      if (conv.isStreaming) {
        ids.add(id);
      }
    }
    return ids;
  }, [state.conversations]);

  const sendMessage = useCallback(
    async (message: string, context?: PageContext) => {
      // Determine which conversation to send to
      // If no active conversation, generate a new ID upfront
      let conversationId = activeConversationId;
      const isNewConversation = !conversationId;

      if (!conversationId) {
        conversationId = generateConversationId();
      }

      // Check if this conversation is already streaming
      const existingConv = state.conversations.get(conversationId);
      if (existingConv?.isStreaming) return;

      // Abort any existing stream for this conversation
      abortControllersRef.current.get(conversationId)?.abort();
      const abortController = new AbortController();
      abortControllersRef.current.set(conversationId, abortController);

      // Build history from derived messages (TanStack + session)
      const conversationHistory = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      dispatch({
        type: 'SEND_MESSAGE',
        conversationId,
        userMessage: message,
      });

      const MAX_RETRIES = 3;

      type StreamResult =
        | { outcome: 'success' }
        | { outcome: 'abort' }
        | { outcome: 'terminal_error'; message: string }
        | { outcome: 'retriable_error' };

      const attemptStream = async (): Promise<StreamResult> => {
        const token =
          localStorage.getItem(getNewWorldTokenLocalStorageKey()) ||
          localStorage.getItem(TOKEN_KEY);

        const response = await fetch(
          `${API_HOST}/provider/rest/chat-agent/stream`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              message,
              conversationHistory,
              context,
              // For existing conversations, send the ID
              // For new conversations, send as newConversationId so server creates with this ID
              conversationId: isNewConversation ? undefined : conversationId,
              newConversationId: isNewConversation ? conversationId : undefined,
            }),
            signal: abortController.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        let receivedContent = false;
        let errorMessage: string | null = null;

        for await (const event of parseServerSentEventsStream(reader)) {
          switch (event.type) {
            case 'text':
              receivedContent = true;
              dispatch({
                type: 'APPEND_TEXT',
                conversationId,
                text: event.data.text,
              });
              break;

            case 'tool_start':
            case 'tool_progress':
              receivedContent = true;
              dispatch({
                type: 'REGISTER_TOOL',
                conversationId,
                toolUseId: event.data.toolUseId,
                toolName: event.data.toolName,
              });
              break;

            case 'message':
              if (event.data.toolUses?.length) {
                receivedContent = true;
                dispatch({
                  type: 'FINALIZE_TOOLS',
                  conversationId,
                  toolUses: event.data.toolUses,
                });

                // Invalidate cache after mutation tools complete
                const hasMutation = event.data.toolUses.some((tool) =>
                  tool.name.toLowerCase().includes('mutation')
                );
                if (hasMutation) {
                  options?.onMutationComplete?.();
                }
              }
              break;

            case 'error':
              if (event.data.code === 'CONTEXT_EXHAUSTED') {
                dispatch({
                  type: 'CONTEXT_EXHAUSTED',
                  conversationId,
                  error: event.data.message,
                });
                return { outcome: 'abort' };
              }
              // Explicit error from server — don't retry
              errorMessage = event.data.message;
              break;

            case 'conversation':
              // Server confirms the conversation ID
              if (isNewConversation) {
                dispatch({
                  type: 'NEW_CONVERSATION_CONFIRMED',
                  conversationId: event.data.conversationId,
                });
                options?.onNewChatConfirmation?.();
              }
              break;

            case 'status':
              dispatch({
                type: 'SET_STATUS',
                conversationId,
                status: event.data.status,
              });
              break;

            case 'result':
              if (event.data.isError && !errorMessage) {
                errorMessage = 'Something went wrong. Please try again.';
              }
              break;

            case 'done':
              // Informational event - no action needed
              break;
          }
        }

        if (errorMessage) {
          return { outcome: 'terminal_error', message: errorMessage };
        }
        if (!receivedContent) {
          return { outcome: 'retriable_error' };
        }
        return { outcome: 'success' };
      };

      try {
        let lastError: string | undefined;
        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          if (attempt > 0) {
            await new Promise((r) => setTimeout(r, 1000 * attempt));
            dispatch({
              type: 'SET_STATUS',
              conversationId,
              status: 'retrying',
            });
          }

          try {
            const result = await attemptStream();
            switch (result.outcome) {
              case 'success':
                dispatch({ type: 'STREAM_COMPLETE', conversationId });
                options?.onStreamComplete?.();
                return;
              case 'abort':
                // CONTEXT_EXHAUSTED was already dispatched
                return;
              case 'terminal_error':
                // Explicit error from server — don't retry
                dispatch({
                  type: 'STREAM_ERROR',
                  conversationId,
                  error: result.message,
                });
                return;
              case 'retriable_error':
                // Empty response — try again
                lastError = 'Something went wrong. Please try again.';
                break;
              default:
                assertUnreachable(result);
            }
          } catch (err) {
            if (err instanceof Error && err.name === 'AbortError') {
              dispatch({ type: 'STREAM_ABORTED', conversationId });
              return;
            }
            lastError =
              err instanceof Error ? err.message : 'An error occurred';
          }
        }

        // All retries exhausted
        dispatch({
          type: 'STREAM_ERROR',
          conversationId,
          error: lastError ?? 'An error occurred',
        });
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          dispatch({ type: 'STREAM_ABORTED', conversationId });
          return;
        }
        dispatch({
          type: 'STREAM_ERROR',
          conversationId,
          error: err instanceof Error ? err.message : 'An error occurred',
        });
      }
    },
    [activeConversationId, state.conversations, messages, options]
  );

  const switchConversation = useCallback(
    (newId: string | null) => {
      const shouldClearPrevious =
        activeConversationId &&
        activeConversation &&
        !activeConversation.isStreaming;

      dispatch({
        type: 'SWITCH_CONVERSATION',
        newConversationId: newId,
        previousConversationId: activeConversationId,
        clearPrevious: !!shouldClearPrevious,
      });

      if (shouldClearPrevious) {
        void queryClient.invalidateQueries({
          queryKey: meKeys.chatAgentConversation(activeConversationId),
        });
      }
    },
    [activeConversationId, activeConversation, queryClient]
  );

  const clearMessages = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return {
    title: conversationData?.chatAgentConversation?.title || undefined,
    summary: conversationData?.chatAgentConversation?.summary || undefined,
    messages,
    isStreaming: activeConversation?.isStreaming ?? false,
    error: activeConversation?.error ?? null,
    isDisabled: activeConversation?.isDisabled ?? false,
    status: activeConversation?.status ?? null,
    conversationId: activeConversationId,
    isLoading,
    streamingConversationIds,
    sendMessage,
    clearMessages,
    switchConversation,
  };
};
